import {
	useState,
	useRef,
	useEffect,
	useMemo,
	useCallback,
	useContext,
} from 'react';
import {
	Flex,
	Box,
	Step,
	StepIndicator,
	StepStatus,
	Stepper,
	StepNumber,
	StepDescription,
	StepSeparator,
} from 'src/components/ui';

import {
	CampaignForm,
	CampaignBrief,
	CampaignBudget,
	DesignOutput,
	CampaignSchedule,
	LaunchCampaign,
} from 'src/components/campaign';
import { CampaignHeader } from 'src/components/campaign/parts';
import {
	AppInputConfigContext,
	CampaignContext,
	withAppConfigProvider,
	withCampaignProvider,
} from 'src/contexts';
import FusionLoading from 'src/components/common/FusionLoading';

import {
	createOrUpdateCampaign,
	genrateCampaignCreatives,
	genrateCampaignDesignDirections,
	getCampaign,
	launchCampaign,
	getInterpretations,
} from 'src/services/campaign';
import {
	ICampaignBrief,
	ICampaignSchedule,
	IChannelCreativeAttributes,
	IChannelGroup,
	IChannelMediaAttributes,
	IDesignDirection,
} from 'src/lib/schemas';
import {
	transformCampaignToGenAIAppPayload,
	transformCampaignToGenAIPayload,
} from 'src/lib/utils';
import useCampaignConfig from 'src/hooks/useCampaignConfig';
import {
	generateCampaignBrief,
	generateCampaignGoals,
} from 'src/services/genai';
import { enrichText } from 'src/services/appsai';
import { processCallback } from 'src/lib/utils/processCallback';
import { getAvailableChannels } from 'src/services/account';
import { generateBudgetBreakdown } from 'src/components/campaign/CampaignBudget/parts/utils';
import { validateCampaignConfig } from 'src/components/campaign/utils/validations';
import { cleanObject_id } from 'src/lib/utils';
import { toastError } from 'src/services/toast';
import { IOption } from 'src/lib/schemas/misc';
import UserContext from 'src/contexts/user/UserContext';
import CustomContainer from 'src/components/common/CustomContainer';
import { CheckIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMyIntegrations } from 'src/contexts/integration/IntegrationContext';
import {
	LEFT_SIDEBAR_WIDTH,
	TOP_BAR_HEIGHT,
	BREAKPOINTS,
	RIGHT_SIDEBAR_WIDTH_COLLAPSED,
} from 'src/components/campaigns/utils/constants';
import useWindowSize from 'src/hooks/useWindowSize';
import { useOnboarding } from 'src/contexts/onboarding';
import { CampaignWizardModal, CampaignStepsOverlay } from 'src/components/onboarding';
import { useCatalogsContext } from 'src/contexts/catalogs/CatalogsContext';

const CreateCampaign = () => {
	const { integrations } = useMyIntegrations();
	const { catalogs } = useCatalogsContext();
	const location = useLocation();
	const navigate = useNavigate();
	const { width } = useWindowSize();
	const isMobile = width < BREAKPOINTS.mobile;

	// Onboarding state for Campaign Wizard (Wizard 2)
	const { wizard2Shown } = useOnboarding();
	const [showCampaignWizard, setShowCampaignWizard] = useState(false);
	const [showStepsOverlay, setShowStepsOverlay] = useState(false);

	// Debug: Log on every render
	console.log('[CreateCampaign] RENDER - wizard2Shown:', wizard2Shown, 'pathname:', location.pathname);

	const [group, setGroup] = useState<string | undefined>();
	const [availableChannelGroups, setAvailableChannelGroups] = useState<
		IChannelGroup[] | null
	>(null);
	const [availableAttributes, setAvailableAttributes] = useState<
		IChannelCreativeAttributes[] | null
	>(null);

	const [isLoadingChannels, setIsLoadingChannels] = useState(false);
	const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
	const [isGeneratingDesignDirections, setIsGeneratingDesignDirections] =
		useState(false);
	const [showDDSection, setShowDDSection] = useState(false);
	const [isBriefAndPromptVisible, setIsBriefAndPromptVisible] = useState(false);
	const [campaignTitle, setCampaignTitle] = useState('');
	const campaignConfigRef = useRef<HTMLDivElement>(null);
	const campaignBriefRef = useRef<HTMLDivElement>(null);
	const campaignDesignRef = useRef<HTMLDivElement>(null);
	const { config, isLoading: isConfigLoading } = useCampaignConfig();
	const [activeStep, setActiveStep] = useState(1);
	const [metaOptions, setMetaOptions] = useState<IOption[]>([]);
	const [scrollPosition, setScrollPosition] = useState(0);
	const [selectedPlacements, setSelectedPlacements] = useState<string[]>([]);

	const {
		campaign,
		id: campaignId,
		isFetching,
		setCampaign,
	} = useContext(CampaignContext);
	const { inputConfig } = useContext(AppInputConfigContext);
	const tonesOptions = inputConfig['Tone'] ?? [];
	const audienceOptions = inputConfig['Audience'] ?? [];
	const { account } = useContext(UserContext);

	// Show campaign wizard modal on first visit to new campaign page
	// Check if this is a "new" campaign route (not editing an existing one)
	const isNewCampaignRoute = location.pathname === '/projects/campaigns/new';
	const wizardCheckedRef = useRef(false);

	useEffect(() => {
		// Check if coming from Shopify or Wizard - don't show wizard in this case
		const searchParams = new URLSearchParams(location.search);
		const fromShopify = searchParams.get('fromShopify') === 'true';
		const fromWizard = searchParams.get('fromWizard') === 'true';
		const fromWordPress = searchParams.get('fromWordPress') === 'true';
		const prefillCatalogId = searchParams.get('prefillCatalogId');

		// Debug logging for Wizard 2
		console.log('🧙 [Wizard 2] Check conditions:', {
			wizard2Shown,
			isNewCampaignRoute,
			pathname: location.pathname,
			wizardCheckedRef: wizardCheckedRef.current,
			fromShopify,
			fromWizard,
			fromWordPress,
			shouldShow: !wizard2Shown && isNewCampaignRoute && !wizardCheckedRef.current && !fromShopify && !fromWizard && !fromWordPress,
		});

		// Only show once per page visit, when on the new campaign route and wizard hasn't been shown before
		// Skip wizard if coming from Shopify, Wizard, or WordPress with a prefilled product
		if (!wizard2Shown && isNewCampaignRoute && !wizardCheckedRef.current && !fromShopify && !fromWizard && !fromWordPress) {
			wizardCheckedRef.current = true;
			console.log('🧙 [Wizard 2] Triggering modal in 500ms...');
			// Small delay to ensure the page has loaded
			setTimeout(() => {
				console.log('🧙 [Wizard 2] Setting showCampaignWizard to true');
				setShowCampaignWizard(true);
			}, 500);
		}
	}, [wizard2Shown, isNewCampaignRoute]);

	const handleCampaignWizardClose = (showOverlay?: boolean) => {
		setShowCampaignWizard(false);
		// Only show steps overlay for manual option (when showOverlay is true)
		if (showOverlay) {
			setShowStepsOverlay(true);
		}
	};

	const handleStepsOverlayDismiss = () => {
		setShowStepsOverlay(false);
	};

	const handleWizardProductSelect = useCallback((product: any) => {
		const productId = product.id || product._id;
		navigate(`/projects/campaigns/new?fromWizard=true&prefillCatalogId=${productId}`, {
			replace: true,
		});
	}, [navigate]);

	useEffect(() => {
		const handleScroll = () => {
			const position = window.scrollY;
			setScrollPosition(position);
		};
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const isDataLoaded = useRef(false);

	useEffect(() => {
		if (!campaign) return;
		let newStep = activeStep;
		if (
			campaign.designDirections &&
			campaign.designDirections.length > 0 &&
			newStep < 2
		) {
			newStep = 2;
		}
		if (campaign?.schedule?.startDate && newStep < 3) {
			newStep = 3;
		}
		if (!isDataLoaded.current && campaign.designDirections !== undefined) {
			isDataLoaded.current = true;
		}
		if (isDataLoaded.current && activeStep !== newStep) {
			setActiveStep(newStep);
		}
	}, [campaign]);

	const steps = [
		{ title: '', description: '' },
		{ title: '', description: 'Generate designs' },
		{ title: '', description: 'Review ads' },
		{ title: '', description: 'Publish ads' },
	];

	useEffect(() => {
		const getChannels = async () => {
			if (!account?.id) return;
			setIsLoadingChannels(true);
			const { channels, creatives } = await getAvailableChannels(account.id);
			setAvailableChannelGroups(channels);
			setAvailableAttributes(creatives);
			setIsLoadingChannels(false);
		};
		getChannels();
	}, [account]);

	const handleCampaignSubmit = async (totalBudget: number) => {
		if (!campaignId || !campaign) return;
		try {
			const budget = { ...campaign.budget, total: totalBudget };
			const genAIPayload = transformCampaignToGenAIPayload(
				{ ...campaign, budget },
				config,
				metaOptions,
				tonesOptions,
				audienceOptions,
			);
			setShowDDSection(true);
			setIsGeneratingBrief(true);
			setTimeout(() => {
				scrollIntoBrief();
			}, 100);
			setIsGeneratingDesignDirections(true);
			const brief = await generateCampaignBrief(genAIPayload);

			const response = await createOrUpdateCampaign({ brief }, campaignId);
			setCampaign(response);
			setIsGeneratingBrief(false);
		} catch (error: any) {
			toastError(error);
			setIsGeneratingBrief(false);
		}
	};

	const scrollIntoBrief = () => {
		if (!campaignBriefRef) return;
		const elementPosition =
			campaignBriefRef!.current!.getBoundingClientRect().top +
			window.pageYOffset;
		const offsetPosition = elementPosition - 150;
		setTimeout(() => {
			window.scrollTo({
				top: offsetPosition,
				behavior: 'smooth',
			});
		}, 100);
	};

	const handleRegenerateBrief = async () => {
		if (!campaignId || !campaign) return;
		try {
			const genAIPayload = transformCampaignToGenAIPayload(
				campaign,
				config,
				metaOptions,
				tonesOptions,
				audienceOptions,
			);
			const brief = await generateCampaignBrief(genAIPayload);
			const response = await createOrUpdateCampaign({ brief }, campaignId);

			setCampaign(response);
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleRegenerateDescription = async () => {
		if (!campaign || !campaignId || !campaign.brief) return;
		try {
			const { callback } = await getInterpretations(campaignId);
			
			const result = await processCallback(callback);
			
			const { brief_description } = result;
			
			const updatedBrief = {
				title: campaign.brief.title,
				description: brief_description,
				...(campaign.brief.goals && campaign.brief.goals.length > 0 
					? { goals: campaign.brief.goals } 
					: {}),
			};
			
			const response = await createOrUpdateCampaign(
				{ brief: updatedBrief },
				campaignId,
			);
			setCampaign(response);
		} catch (error: any) {
			console.error('❌ [REGENERATE] Error:', error);
			toastError(error);
		}
	};

	const handleRegenerateGoals = async () => {
		if (!campaign || !campaignId || !campaign.brief) return;
		try {
			const genAIPayload = transformCampaignToGenAIPayload(
				campaign,
				config,
				metaOptions,
				tonesOptions,
				audienceOptions,
			);
			const goals = await generateCampaignGoals(genAIPayload);
			const response = await createOrUpdateCampaign(
				{
					brief: {
						...cleanObject_id(campaign.brief),
						goals,
					},
				},
				campaignId,
			);
			setCampaign(response);
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleEnrichDescription = async (text: string) => {
		if (!campaign || !campaignId || !campaign.brief) return;
		try {
			const genAIPayload = transformCampaignToGenAIAppPayload(
				campaign,
				config,
				metaOptions,
				tonesOptions,
				audienceOptions,
				account,
				text,
			);
			const description = await enrichText(genAIPayload);
			const response = await createOrUpdateCampaign(
				{ brief: { ...cleanObject_id(campaign.brief), description } },
				campaignId,
			);
			setCampaign(response);
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleSaveBrief = async (brief: ICampaignBrief) => {
		if (!campaignId || !campaign) return;
		try {
			const resp = await createOrUpdateCampaign(
				{ brief: cleanObject_id(brief) },
				campaignId,
			);
			setCampaign(resp);
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleGenerateCreatives = async (designDirectionId?: string) => {
		if (!campaignId) return;
		try {
			// await executeCampaignCreatives(campaignId, campaign?.brief?.prompt);
			await genrateCampaignCreatives(campaignId, designDirectionId);
			const data = await getCampaign(campaignId);
			setCampaign(data);
		} catch (error: any) {
			toastError(error);
		}
	};

	useEffect(() => {
		if (campaign?.creatives?.length && !campaign?.budget.breakdown?.length) {
			handleGenerateBudgetBreakdown();
		}
	}, [campaign?.creatives]);

	const handleGenerateDesignDirections = async () => {
		if (!campaignId) return;
		try {
			await genrateCampaignDesignDirections(campaignId);
			const data = await getCampaign(campaignId);
			setCampaign(data);
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleGenerateBudgetBreakdown = async () => {
		if (!campaignId || !campaign) return;
		try {
			const initialBreakdown = generateBudgetBreakdown(
				campaign.channels ?? [],
				availableChannelGroups ?? [],
				campaign.budget.total,
			);
			const response = await createOrUpdateCampaign(
				{
					budget: { ...campaign.budget, breakdown: initialBreakdown },
				},
				campaignId,
			);
			setCampaign(response);
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleSubmitSchedule = async (schedule: ICampaignSchedule) => {
		if (!campaignId || !campaign) return;
		try {
			const response = await createOrUpdateCampaign(
				{
					schedule,
				},
				campaignId,
			);
			setCampaign(response);
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleLaunch = async () => {
		if (!campaignId) return;
		await launchCampaign(campaignId);
	};

	const handleScrollToView = (ref: React.RefObject<HTMLDivElement>) => {
		setTimeout(() => {
			ref?.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}, 100);
	};

	const isLoading = isFetching || isConfigLoading || isLoadingChannels;

	const availableChannels = useMemo(
		() =>
			(availableChannelGroups || []).flatMap(
				(channelGroup) => channelGroup.channels,
			),
		[availableChannelGroups],
	);

	const birefDetails = useMemo(() => {
		const campaignTones = campaign?.tone || [];
		const campaignChannels = campaign?.channels || [];
		const campaignAudience = campaign?.audience || [];

		const mappedChannels = (availableChannels || [])
			.filter((c) => campaignChannels.includes(c.id))
			.map((c) => c.name);

		const mappedTones = tonesOptions
			.filter((t) => campaignTones.includes(t.value))
			.map((t) => t.label);

		const mappedAudience = audienceOptions
			.filter((t) => campaignAudience.includes(t.value))
			.map((t) => t.label);

		return {
			budget: campaign?.budget?.total || 0,
			channels: mappedChannels,
			tones: mappedTones,
			audience: mappedAudience,
		};
	}, [campaign, availableChannels, tonesOptions, audienceOptions]);

	const handleMetaOptions = useCallback((options: IOption[]) => {
		if (options.length) {
			setMetaOptions(options);
		}
	}, []);

	const removeDuplicateLayers = useCallback((data?: IDesignDirection[]) => {
		if (!data) return [];
		return data.map((item: any) => {
			const attributes = item.attributes as IChannelMediaAttributes;
			const layers = attributes.image.layers;
			const uniqueLayers: any[] = [];
			const layerNames = new Set();

			if (!layers) return item;
			layers.forEach((layer) => {
				if (!layerNames.has(layer.name)) {
					uniqueLayers.push(layer);
					layerNames.add(layer.name);
				}
			});

			return {
				...item,
				attributes: {
					...item.attributes,
					image: {
						...item.attributes.image,
						layers: uniqueLayers,
					},
				},
			};
		});
	}, []);

	const memorizedDesignDirections = useMemo(() => {
		return removeDuplicateLayers(campaign?.designDirections);
	}, [campaign?.designDirections, removeDuplicateLayers]);

	useEffect(() => {
		if (location.state?.selectedChannel && campaignId) {
			const selectedChannel = availableChannels.find(
				(channel) => channel.id === location.state.selectedChannel,
			);

			// Check if selectedChannel is found and has placements
			if (selectedChannel && selectedChannel.placements) {
				const placements = selectedChannel.placements.map(
					(placement: any) => placement.id,
				);
				const channel = location.state.selectedChannel;

				createOrUpdateCampaign({ channels: [channel], placements }, campaignId);
			}
		}
	}, [location.state, campaignId, availableChannels]);

	useEffect(() => {
		if (campaign?.group) {
			setGroup(campaign.group);
		}
	}, [campaign]);

	useEffect(() => {
		Boolean(campaign?.brief) && setShowDDSection(true);
	}, [campaign]);

	const isValidCampaignConfig = useMemo(() => {
		if (!campaign) return false;
		return validateCampaignConfig(campaign, metaOptions ?? []);
	}, [campaign, metaOptions]);

	const isLaunchEnabled = !!campaign?.schedule;

	// Debug logging for mobile layout
	console.log('📱 CreateCampaign Mobile Debug:', {
		isMobile,
		width,
		screenWidth: typeof window !== 'undefined' ? window.screen.width : 0,
		windowWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
		BREAKPOINTS_MOBILE: BREAKPOINTS.mobile,
		campaignTitle: !campaignTitle ? 'New Campaign' : campaignTitle,
		group,
	});

	return (
		<Box w="full" position="relative">
			<Box display={{ base: 'block', md: 'none' }}>
				<CampaignHeader
					campaignDetail={{
						title: !campaignTitle ? 'New Campaign' : campaignTitle,
						group,
						lastEditTime: campaign?.updatedAt,
						lastEditBy: campaign?.lastUpdatedBy?.name,
					}}
					onGroupChange={(value) => setGroup(value)}
				/>
			</Box>
			<Box display={{ base: 'none', md: 'block' }}>
				<CampaignHeader
					campaignDetail={{
						title: !campaignTitle ? 'New Campaign' : campaignTitle,
						group,
						lastEditTime: campaign?.updatedAt,
						lastEditBy: campaign?.lastUpdatedBy?.name,
					}}
					onGroupChange={(value) => setGroup(value)}
				/>
			</Box>
			<Box
				position={{ base: 'relative', md: 'fixed' }}
				top={{ base: 'auto', md: scrollPosition > 50 ? TOP_BAR_HEIGHT : '50px' }}
				left={{ base: 'auto', md: '0' }}
				right={{ base: 'auto', md: RIGHT_SIDEBAR_WIDTH_COLLAPSED }}
				zIndex={10}
				backgroundColor="white"
				px="4px"
				py={1}
			>
				<Flex justify="center" p={1} direction="column">
					<Stepper size="xs" index={activeStep} gap="0" colorScheme="red">
						{steps.map((step, index) => (
							<Step key={index}>
								<Flex direction="row" align="center">
									<StepIndicator bg="white" mr={index === 0 ? '-10px' : 1}>
										<StepStatus
											complete={index === 0 ? <Box /> : <Box display="flex" justifyContent="center" alignItems="center"><CheckIcon /></Box>}
											incomplete={<StepNumber style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '0px', lineHeight: '0px' }}>{index}</StepNumber>}
											active={<StepNumber style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '0px', lineHeight: '0px' }}>{index}</StepNumber>}
										/>
									</StepIndicator>
									<Box><StepDescription>{step.description}</StepDescription></Box>
								</Flex>
								{index < steps.length - 1 && <Box as={StepSeparator} />}
							</Step>
						))}
					</Stepper>
				</Flex>
			</Box>

			<FusionLoading isLoading={isLoading} boxProps={{ mt: 16 }} />
			<Flex
				display={isLoading ? 'none' : 'flex'}
				direction="column"
				mt={{ base: 3, md: 0 }}
				h={{ base: 'auto', md: 'auto' }}
				minH={{ base: 'auto', md: 'auto' }}
				px={{ base: 2, md: 0 }}
				pb={{ base: 4, md: 0 }}
			>
				<Box ref={campaignConfigRef} />
				<CustomContainer>
					<CampaignForm
						group={group}
						availableChannels={availableChannels}
						onCampaignTitleChange={setCampaignTitle}
						onCampaignSubmit={handleCampaignSubmit}
						onMetaOptionsChange={handleMetaOptions}
						selectedPlacements={selectedPlacements}
						setSelectedPlacements={setSelectedPlacements}
					/>
					<Box ref={campaignBriefRef} />
					<CampaignBrief
						brief={campaign?.brief}
						birefDetails={birefDetails}
						isValidCampaignConfig={isValidCampaignConfig}
						onRegenerateBrief={handleRegenerateBrief}
						onRegenerateGoals={handleRegenerateGoals}
						onRegenerateDescription={handleRegenerateDescription}
						onEnrichDescription={handleEnrichDescription}
						onSaveBrief={handleSaveBrief}
						isGeneratingBrief={isGeneratingBrief}
						campaignTitle={campaignTitle}
					/>
					<Box ref={campaignDesignRef} />
					{showDDSection && (
						<DesignOutput
							creatives={campaign?.creatives}
							designDirections={memorizedDesignDirections}
							channelCreativeAttributes={availableAttributes}
							onGenerateCreatives={handleGenerateCreatives}
							onGenerateDesignDirections={handleGenerateDesignDirections}
							setIsBriefAndPromptVisible={setIsBriefAndPromptVisible}
							isBriefAndPromptVisible={isBriefAndPromptVisible}
							isGeneratingDesignDirections={isGeneratingDesignDirections}
							setIsGeneratingDesignDirections={setIsGeneratingDesignDirections}
						/>
					)}
					{!!campaign?.creatives?.length && (
						<>
							{!!campaign?.creatives?.length && (
								<CampaignSchedule
									schedule={campaign?.schedule}
									onScheduleSubmit={handleSubmitSchedule}
								/>
							)}
							<Box mt={4} />
							{!!campaign?.budget.breakdown?.length &&
								!!availableChannelGroups && (
									<CampaignBudget
										availableChannelGroups={availableChannelGroups}
									/>
								)}

							<LaunchCampaign
								isValidCampaignConfig={isValidCampaignConfig}
								isSocialEnabled={integrations?.social.fb.enabled}
								onLaunch={handleLaunch}
							/>
						</>
					)}
				</CustomContainer>
			</Flex>

			{/* Campaign Wizard Modal (Wizard 2) */}
			<CampaignWizardModal
				isOpen={showCampaignWizard}
				onClose={handleCampaignWizardClose}
				products={catalogs}
				onProductSelect={handleWizardProductSelect}
			/>

			{/* Campaign Steps Overlay */}
			<CampaignStepsOverlay
				isVisible={showStepsOverlay}
				onDismiss={handleStepsOverlayDismiss}
			/>
		</Box>
	);
};

const CreateCampaignWithProvider = withCampaignProvider(CreateCampaign);
const CreateCampaignWithAppConfigProvider = withAppConfigProvider(
	CreateCampaignWithProvider,
);

export default CreateCampaignWithAppConfigProvider;
